import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAllDocuments, getDocumentStatistics } from '../../services/documentService';
import { 
  FileText, 
  Download, 
  Eye, 
  Search, 
  Filter, 
  Calendar, 
  User, 
  Building2, 
  Stethoscope, 
  CreditCard, 
  FileCheck,
  Upload,
  Archive,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Folder,
  FolderOpen,
  Grid,
  List,
  SortAsc,
  SortDesc
} from 'lucide-react';

const DocumentsPage = () => {
  const { userData } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedUserType, setSelectedUserType] = useState('all');
  const [sortBy, setSortBy] = useState('uploadedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // Document categories based on the application's document types
  const documentCategories = {
    all: { label: 'All Documents', icon: FolderOpen, color: 'text-gray-600', bgColor: 'bg-gray-100' },
    medical: { label: 'Medical Documents', icon: Stethoscope, color: 'text-green-600', bgColor: 'bg-green-100' },
    policy: { label: 'Policy Documents', icon: FileCheck, color: 'text-blue-600', bgColor: 'bg-blue-100' },
    financial: { label: 'Financial Documents', icon: CreditCard, color: 'text-purple-600', bgColor: 'bg-purple-100' },
    kyc: { label: 'KYC Documents', icon: User, color: 'text-orange-600', bgColor: 'bg-orange-100' },
    hospital: { label: 'Hospital Documents', icon: Building2, color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
    discharge: { label: 'Discharge Documents', icon: Archive, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
    prescription: { label: 'Prescriptions', icon: FileText, color: 'text-pink-600', bgColor: 'bg-pink-100' },
    bills: { label: 'Bills & Invoices', icon: CreditCard, color: 'text-red-600', bgColor: 'bg-red-100' },
    additional: { label: 'Additional Documents', icon: Upload, color: 'text-teal-600', bgColor: 'bg-teal-100' }
  };

  // User type filters
  const userTypes = {
    all: { label: 'All Users', icon: User, color: 'text-gray-600' },
    hospital: { label: 'Hospital', icon: Building2, color: 'text-blue-600' },
    doctor: { label: 'Doctor', icon: Stethoscope, color: 'text-green-600' },
    policy: { label: 'Policy Writer', icon: FileCheck, color: 'text-orange-600' },
    finance: { label: 'Financial Team', icon: CreditCard, color: 'text-purple-600' },
    admin: { label: 'Admin', icon: Star, color: 'text-red-600' }
  };

  // Document status options
  const documentStatuses = {
    all: { label: 'All Status', color: 'text-gray-600' },
    approved: { label: 'Approved', color: 'text-green-600', icon: CheckCircle },
    pending: { label: 'Pending', color: 'text-yellow-600', icon: Clock },
    rejected: { label: 'Rejected', color: 'text-red-600', icon: XCircle },
    under_review: { label: 'Under Review', color: 'text-blue-600', icon: AlertCircle }
  };

  // Sort options
  const sortOptions = {
    uploadedAt: { label: 'Upload Date', icon: Calendar },
    fileName: { label: 'File Name', icon: FileText },
    size: { label: 'File Size', icon: Archive },
    uploadedBy: { label: 'Uploaded By', icon: User },
    category: { label: 'Category', icon: Folder }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    filterAndSortDocuments();
  }, [documents, searchTerm, selectedCategory, selectedStatus, selectedUserType, sortBy, sortOrder]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const result = await getAllDocuments();
      if (result.success) {
        setDocuments(result.documents);
      } else {
        console.error('Error loading documents:', result.error);
        // Fallback to mock data for demonstration
        const mockDocuments = generateMockDocuments();
        setDocuments(mockDocuments);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      // Fallback to mock data for demonstration
      const mockDocuments = generateMockDocuments();
      setDocuments(mockDocuments);
    } finally {
      setLoading(false);
    }
  };

  const generateMockDocuments = () => {
    const baseDocuments = [
      // Medical Documents
      {
        id: '1',
        fileName: 'Patient_Medical_Report_001.pdf',
        title: 'Medical Report - Patient 001',
        category: 'medical',
        type: 'medical_document',
        size: 2048576, // 2MB
        uploadedAt: new Date('2024-01-15'),
        uploadedBy: 'doctor',
        uploaderName: 'Dr. Smith',
        status: 'approved',
        url: '#',
        description: 'Comprehensive medical examination report',
        patientId: 'PAT001',
        applicationId: 'APP001'
      },
      {
        id: '2',
        fileName: 'Prescription_Medication_List.pdf',
        title: 'Prescription - Medication List',
        category: 'prescription',
        type: 'prescription',
        size: 512000, // 512KB
        uploadedAt: new Date('2024-01-14'),
        uploadedBy: 'doctor',
        uploaderName: 'Dr. Johnson',
        status: 'approved',
        url: '#',
        description: 'Prescribed medications and dosages',
        patientId: 'PAT002',
        applicationId: 'APP002'
      },
      // Policy Documents
      {
        id: '3',
        fileName: 'Insurance_Policy_Copy.pdf',
        title: 'Insurance Policy Document',
        category: 'policy',
        type: 'policy_document',
        size: 1536000, // 1.5MB
        uploadedAt: new Date('2024-01-13'),
        uploadedBy: 'policy',
        uploaderName: 'Policy Writer',
        status: 'approved',
        url: '#',
        description: 'Complete insurance policy terms and conditions',
        patientId: 'PAT003',
        applicationId: 'APP003'
      },
      // Financial Documents
      {
        id: '4',
        fileName: 'Hospital_Bill_Invoice.pdf',
        title: 'Hospital Bill Invoice',
        category: 'bills',
        type: 'bill',
        size: 1024000, // 1MB
        uploadedAt: new Date('2024-01-12'),
        uploadedBy: 'hospital',
        uploaderName: 'Hospital Admin',
        status: 'pending',
        url: '#',
        description: 'Detailed hospital bill with itemized charges',
        patientId: 'PAT004',
        applicationId: 'APP004'
      },
      // KYC Documents
      {
        id: '5',
        fileName: 'Aadhaar_Card_Verification.pdf',
        title: 'Aadhaar Card Verification',
        category: 'kyc',
        type: 'kyc_document',
        size: 768000, // 768KB
        uploadedAt: new Date('2024-01-11'),
        uploadedBy: 'hospital',
        uploaderName: 'Hospital Staff',
        status: 'approved',
        url: '#',
        description: 'Aadhaar card verification document',
        patientId: 'PAT005',
        applicationId: 'APP005'
      },
      // Hospital Documents
      {
        id: '6',
        fileName: 'Hospital_Registration_Certificate.pdf',
        title: 'Hospital Registration Certificate',
        category: 'hospital',
        type: 'registration_certificate',
        size: 2560000, // 2.5MB
        uploadedAt: new Date('2024-01-10'),
        uploadedBy: 'hospital',
        uploaderName: 'Hospital Admin',
        status: 'approved',
        url: '#',
        description: 'Official hospital registration certificate',
        hospitalId: 'HOS001'
      },
      // Discharge Documents
      {
        id: '7',
        fileName: 'Discharge_Summary_Report.pdf',
        title: 'Discharge Summary Report',
        category: 'discharge',
        type: 'discharge_summary',
        size: 1280000, // 1.28MB
        uploadedAt: new Date('2024-01-09'),
        uploadedBy: 'doctor',
        uploaderName: 'Dr. Williams',
        status: 'under_review',
        url: '#',
        description: 'Comprehensive discharge summary with treatment details',
        patientId: 'PAT006',
        applicationId: 'APP006'
      },
      // Additional Documents
      {
        id: '8',
        fileName: 'Additional_Supporting_Documents.pdf',
        title: 'Additional Supporting Documents',
        category: 'additional',
        type: 'additional_document',
        size: 896000, // 896KB
        uploadedAt: new Date('2024-01-08'),
        uploadedBy: 'hospital',
        uploaderName: 'Hospital Staff',
        status: 'pending',
        url: '#',
        description: 'Additional supporting documents for claim processing',
        patientId: 'PAT007',
        applicationId: 'APP007'
      }
    ];

    // Generate more documents for a comprehensive view
    const additionalDocuments = [];
    for (let i = 9; i <= 50; i++) {
      const categories = Object.keys(documentCategories).filter(cat => cat !== 'all');
      const userTypes = ['hospital', 'doctor', 'policy', 'finance', 'admin'];
      const statuses = ['approved', 'pending', 'rejected', 'under_review'];
      
      const randomCategory = categories[Math.floor(Math.random() * categories.length)];
      const randomUserType = userTypes[Math.floor(Math.random() * userTypes.length)];
      const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
      
      additionalDocuments.push({
        id: i.toString(),
        fileName: `Document_${i.toString().padStart(3, '0')}.pdf`,
        title: `Document ${i} - ${randomCategory.charAt(0).toUpperCase() + randomCategory.slice(1)}`,
        category: randomCategory,
        type: `${randomCategory}_document`,
        size: Math.floor(Math.random() * 3000000) + 100000, // Random size between 100KB and 3MB
        uploadedAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000), // Random date within last 30 days
        uploadedBy: randomUserType,
        uploaderName: `${randomUserType.charAt(0).toUpperCase() + randomUserType.slice(1)} User`,
        status: randomStatus,
        url: '#',
        description: `Sample document ${i} for ${randomCategory} category`,
        patientId: `PAT${i.toString().padStart(3, '0')}`,
        applicationId: `APP${i.toString().padStart(3, '0')}`
      });
    }

    return [...baseDocuments, ...additionalDocuments];
  };

  const filterAndSortDocuments = () => {
    let filtered = [...documents];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }

    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(doc => doc.status === selectedStatus);
    }

    // Apply user type filter
    if (selectedUserType !== 'all') {
      filtered = filtered.filter(doc => doc.uploadedBy === selectedUserType);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'fileName':
          aValue = a.fileName.toLowerCase();
          bValue = b.fileName.toLowerCase();
          break;
        case 'size':
          aValue = a.size;
          bValue = b.size;
          break;
        case 'uploadedBy':
          aValue = a.uploaderName.toLowerCase();
          bValue = b.uploaderName.toLowerCase();
          break;
        case 'category':
          aValue = a.category.toLowerCase();
          bValue = b.category.toLowerCase();
          break;
        default: // uploadedAt
          aValue = new Date(a.uploadedAt);
          bValue = new Date(b.uploadedAt);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredDocuments(filtered);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    const statusConfig = documentStatuses[status];
    return statusConfig?.icon || AlertCircle;
  };

  const getStatusColor = (status) => {
    const statusConfig = documentStatuses[status];
    return statusConfig?.color || 'text-gray-600';
  };

  const openDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const downloadDocument = (document) => {
    // In a real implementation, this would trigger actual download
    console.log('Downloading document:', document.fileName);
    // For now, we'll just show an alert
    alert(`Downloading ${document.fileName}`);
  };

  const getCategoryStats = () => {
    const stats = {};
    Object.keys(documentCategories).forEach(category => {
      if (category !== 'all') {
        stats[category] = documents.filter(doc => doc.category === category).length;
      }
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Documents Center</h1>
                <p className="mt-2 text-gray-600">
                  Access and manage all documents across the platform
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  Total: {documents.length} documents
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <Grid className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <List className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Document Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Object.entries(documentCategories).map(([key, category]) => {
              if (key === 'all') return null;
              const Icon = category.icon;
              const count = categoryStats[key] || 0;
              return (
                <div
                  key={key}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedCategory === key
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedCategory(key)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${category.bgColor}`}>
                      <Icon className={`h-5 w-5 ${category.color}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{category.label}</p>
                      <p className="text-sm text-gray-500">{count} documents</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.entries(documentCategories).map(([key, category]) => (
                  <option key={key} value={key}>{category.label}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.entries(documentStatuses).map(([key, status]) => (
                  <option key={key} value={key}>{status.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* User Type Filter */}
              <div>
                <select
                  value={selectedUserType}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(userTypes).map(([key, userType]) => (
                    <option key={key} value={key}>{userType.label}</option>
                  ))}
                </select>
              </div>

              {/* Sort By */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(sortOptions).map(([key, option]) => (
                    <option key={key} value={key}>{option.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="text-sm text-gray-500">
              Showing {filteredDocuments.length} of {documents.length} documents
            </div>
          </div>
        </div>

        {/* Documents Grid/List */}
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
            <p className="text-gray-500">Try adjusting your search criteria or filters.</p>
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
          }>
            {filteredDocuments.map((document) => {
              const categoryConfig = documentCategories[document.category];
              const StatusIcon = getStatusIcon(document.status);
              const statusColor = getStatusColor(document.status);

              if (viewMode === 'grid') {
                return (
                  <div key={document.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${categoryConfig.bgColor}`}>
                          <FileText className={`h-5 w-5 ${categoryConfig.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{document.title}</h3>
                          <p className="text-sm text-gray-500 truncate">{document.fileName}</p>
                        </div>
                      </div>
                      <div className={`flex items-center space-x-1 ${statusColor}`}>
                        <StatusIcon className="h-4 w-4" />
                        <span className="text-xs font-medium capitalize">{document.status}</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Size:</span>
                        <span className="text-gray-900">{formatFileSize(document.size)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Uploaded:</span>
                        <span className="text-gray-900">{formatDate(document.uploadedAt)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">By:</span>
                        <span className="text-gray-900">{document.uploaderName}</span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{document.description}</p>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => openDocument(document)}
                        className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center space-x-1"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => downloadDocument(document)}
                        className="flex-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 flex items-center justify-center space-x-1"
                      >
                        <Download className="h-4 w-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                );
              } else {
                // List view
                return (
                  <div key={document.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg ${categoryConfig.bgColor}`}>
                        <FileText className={`h-6 w-6 ${categoryConfig.color}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-medium text-gray-900 truncate">{document.title}</h3>
                          <div className={`flex items-center space-x-1 ${statusColor}`}>
                            <StatusIcon className="h-4 w-4" />
                            <span className="text-xs font-medium capitalize">{document.status}</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{document.fileName}</p>
                        <p className="text-sm text-gray-600 mt-1">{document.description}</p>
                      </div>

                      <div className="text-right text-sm text-gray-500 space-y-1">
                        <div>{formatFileSize(document.size)}</div>
                        <div>{formatDate(document.uploadedAt)}</div>
                        <div>{document.uploaderName}</div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => openDocument(document)}
                          className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center space-x-1"
                        >
                          <Eye className="h-4 w-4" />
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => downloadDocument(document)}
                          className="border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 flex items-center space-x-1"
                        >
                          <Download className="h-4 w-4" />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}

        {/* Document Modal */}
        {showDocumentModal && selectedDocument && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedDocument.title}</h2>
                  <p className="text-sm text-gray-500">{selectedDocument.fileName}</p>
                </div>
                <button
                  onClick={() => setShowDocumentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">File Size</label>
                      <p className="text-gray-900">{formatFileSize(selectedDocument.size)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Uploaded By</label>
                      <p className="text-gray-900">{selectedDocument.uploaderName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Upload Date</label>
                      <p className="text-gray-900">{formatDate(selectedDocument.uploadedAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <div className={`flex items-center space-x-1 ${getStatusColor(selectedDocument.status)}`}>
                        {React.createElement(getStatusIcon(selectedDocument.status), { className: "h-4 w-4" })}
                        <span className="capitalize">{selectedDocument.status}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <p className="text-gray-900">{selectedDocument.description}</p>
                  </div>
                </div>
                
                <div className="mt-6 flex space-x-3">
                  <button
                    onClick={() => downloadDocument(selectedDocument)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Document</span>
                  </button>
                  <button
                    onClick={() => setShowDocumentModal(false)}
                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
